import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getFeatureFlags,
  createFeatureFlag,
  updateFeatureFlag,
  toggleFeatureFlag,
  deleteFeatureFlag,
} from "../../../data";
import { AdminLayout } from "../shared/layout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "@/components/ui/use-toast";
import {
  Flag,
  Plus,
  Pencil,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Smartphone,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export function FeatureFlagsScreen() {
  const queryClient = useQueryClient();
  const [manageDialog, setManageDialog] = useState({ open: false, flag: null });
  const [formData, setFormData] = useState({
    key: "",
    name: "",
    description: "",
    isEnabled: false,
    platform: "all",
  });

  const { data, isLoading } = useQuery({
    queryKey: ["feature-flags"],
    queryFn: getFeatureFlags,
  });

  const createMutation = useMutation({
    mutationFn: createFeatureFlag,
    onSuccess: () => {
      queryClient.invalidateQueries(["feature-flags"]);
      setManageDialog({ open: false, flag: null });
      resetForm();
      toast({
        title: "Başarılı",
        description: "Feature flag oluşturuldu",
        variant: "success",
      });
    },
    onError: (error) => {
      toast({
        title: "Hata",
        description: error.response?.data?.message || "İşlem başarısız",
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => updateFeatureFlag(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["feature-flags"]);
      setManageDialog({ open: false, flag: null });
      resetForm();
      toast({
        title: "Başarılı",
        description: "Feature flag güncellendi",
        variant: "success",
      });
    },
    onError: (error) => {
      toast({
        title: "Hata",
        description: error.response?.data?.message || "İşlem başarısız",
        variant: "destructive",
      });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: toggleFeatureFlag,
    onSuccess: () => {
      queryClient.invalidateQueries(["feature-flags"]);
      toast({
        title: "Başarılı",
        description: "Feature flag durumu değiştirildi",
        variant: "success",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteFeatureFlag,
    onSuccess: () => {
      queryClient.invalidateQueries(["feature-flags"]);
      toast({
        title: "Başarılı",
        description: "Feature flag silindi",
        variant: "success",
      });
    },
  });

  const resetForm = () => {
    setFormData({
      key: "",
      name: "",
      description: "",
      isEnabled: false,
      platform: "all",
    });
  };

  const handleCreate = () => {
    setManageDialog({ open: true, flag: null });
    resetForm();
  };

  const handleEdit = (flag) => {
    setManageDialog({ open: true, flag });
    setFormData({
      key: flag.key,
      name: flag.name,
      description: flag.description || "",
      isEnabled: flag.isEnabled,
      platform: flag.platform,
    });
  };

  const handleSubmit = () => {
    if (manageDialog.flag) {
      // Update
      updateMutation.mutate({
        id: manageDialog.flag.id,
        data: {
          name: formData.name,
          description: formData.description,
          isEnabled: formData.isEnabled,
          platform: formData.platform,
        },
      });
    } else {
      // Create
      createMutation.mutate(formData);
    }
  };

  const getPlatformBadge = (platform) => {
    const colors = {
      all: "bg-blue-100 text-blue-800",
      ios: "bg-gray-100 text-gray-800",
      android: "bg-green-100 text-green-800",
    };

    return (
      <Badge variant="outline" className={colors[platform?.toLowerCase()] || ""}>
        {platform?.toUpperCase() || "ALL"}
      </Badge>
    );
  };

  const flags = data?.data?.data || [];

  const actions = [
    {
      name: "Yeni Feature Flag",
      icon: <Plus className="mr-2 h-4 w-4" />,
      onClick: handleCreate,
    },
  ];

  return (
    <AdminLayout
      title="Feature Flags"
      description="Uygulama özelliklerini runtime'da açıp kapatın"
      loading={isLoading}
      actions={actions}
    >
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Flag className="mr-2 h-5 w-5" />
            Feature Flags ({flags.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {flags.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Flag className="mx-auto h-12 w-12 mb-4 text-gray-300" />
              <p>Henüz feature flag bulunmuyor.</p>
              <Button onClick={handleCreate} className="mt-4">
                <Plus className="mr-2 h-4 w-4" />
                İlk Feature Flag'i Oluştur
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Key</TableHead>
                  <TableHead>İsim</TableHead>
                  <TableHead>Durum</TableHead>
                  <TableHead>Platform</TableHead>
                  <TableHead>Açıklama</TableHead>
                  <TableHead>İşlemler</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {flags.map((flag) => (
                  <TableRow key={flag.id}>
                    <TableCell>
                      <code className="text-sm bg-gray-100 px-2 py-1 rounded">
                        {flag.key}
                      </code>
                    </TableCell>
                    <TableCell className="font-medium">{flag.name}</TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <Switch
                          checked={flag.isEnabled}
                          onCheckedChange={() => toggleMutation.mutate(flag.id)}
                          disabled={toggleMutation.isLoading}
                        />
                        <Badge variant={flag.isEnabled ? "success" : "secondary"}>
                          {flag.isEnabled ? (
                            <>
                              <ToggleRight className="mr-1 h-3 w-3" />
                              Aktif
                            </>
                          ) : (
                            <>
                              <ToggleLeft className="mr-1 h-3 w-3" />
                              Pasif
                            </>
                          )}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell>{getPlatformBadge(flag.platform)}</TableCell>
                    <TableCell>
                      <div className="max-w-xs truncate text-sm text-gray-600">
                        {flag.description || "—"}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(flag)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Flag'i Sil</AlertDialogTitle>
                              <AlertDialogDescription>
                                Bu feature flag'i silmek istediğinizden emin misiniz?
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>İptal</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => deleteMutation.mutate(flag.id)}
                              >
                                Sil
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog
        open={manageDialog.open}
        onOpenChange={(open) => {
          setManageDialog({ open, flag: manageDialog.flag });
          if (!open) resetForm();
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {manageDialog.flag ? "Feature Flag Düzenle" : "Yeni Feature Flag"}
            </DialogTitle>
            <DialogDescription>
              {manageDialog.flag
                ? "Feature flag bilgilerini güncelleyin"
                : "Yeni bir feature flag oluşturun"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="key">Key * (değiştirilemez)</Label>
              <Input
                id="key"
                placeholder="new_feature_enabled"
                value={formData.key}
                onChange={(e) => setFormData({ ...formData, key: e.target.value })}
                disabled={!!manageDialog.flag}
              />
              <p className="text-xs text-gray-500">
                Küçük harf, rakam ve alt çizgi kullanın
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">İsim *</Label>
              <Input
                id="name"
                placeholder="Yeni Özellik"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Açıklama</Label>
              <Textarea
                id="description"
                placeholder="Bu özellik hakkında açıklama..."
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="platform">Platform</Label>
              <Select
                value={formData.platform}
                onValueChange={(value) =>
                  setFormData({ ...formData, platform: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm Platformlar</SelectItem>
                  <SelectItem value="ios">iOS</SelectItem>
                  <SelectItem value="android">Android</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="enabled"
                checked={formData.isEnabled}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, isEnabled: checked })
                }
              />
              <Label htmlFor="enabled">Aktif olarak oluştur</Label>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setManageDialog({ open: false, flag: null });
                resetForm();
              }}
            >
              İptal
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={
                !formData.key ||
                !formData.name ||
                createMutation.isLoading ||
                updateMutation.isLoading
              }
            >
              {createMutation.isLoading || updateMutation.isLoading
                ? "İşleniyor..."
                : manageDialog.flag
                ? "Güncelle"
                : "Oluştur"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}

